import type { SchoolImpactReportGrading } from '@whizz/api-model';
import React from 'react';
import { DateTime } from 'luxon';

type Props = {
  grading?: SchoolImpactReportGrading | null;
};

type DecisionMeta = {
  label: string;
  description: string;
  accentClassName: string;
  pillClassName: string;
};

const CATEGORY_LABELS: Record<keyof SchoolImpactReportGrading['categoryScores'], string> = {
  accuracy: 'Accuracy',
  completeness: 'Completeness',
  insightfulness: 'Insightfulness',
  recommendations: 'Recommendations',
  tone: 'Tone',
  clarity: 'Clarity',
};

const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS) as (keyof SchoolImpactReportGrading['categoryScores'])[];

const DECISION_META: Record<SchoolImpactReportGrading['sendDecision'], DecisionMeta> = {
  auto_send: {
    label: 'Ready to send',
    description: 'The automated review found no issues that should stop this report being sent.',
    accentClassName: 'border-green-200 bg-green-50 text-green-900',
    pillClassName: 'bg-green-100 text-green-800',
  },
  human_review: {
    label: 'Needs human review',
    description: 'The report looks usable, but it should be checked by a person before it is sent.',
    accentClassName: 'border-yellow-200 bg-yellow-50 text-yellow-900',
    pillClassName: 'bg-yellow-100 text-yellow-800',
  },
  block: {
    label: 'Do not send yet',
    description: 'The automated review found issues that should be fixed before this report is sent.',
    accentClassName: 'border-red-200 bg-red-50 text-red-900',
    pillClassName: 'bg-red-100 text-red-800',
  },
};

const NUMBER_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
});

const humanizeToken = (value: string): string => value
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatScore = (score: number): string => {
  if (!Number.isFinite(score)) {
    return 'N/A';
  }

  return NUMBER_FORMATTER.format(score);
};

const formatDate = (value: string): string => {
  const date = DateTime.fromISO(value);

  if (!date.isValid) {
    return value;
  }

  return date.toFormat('dd LLL yyyy, HH:mm');
};

const ImpactReportGradingSummary = ({ grading }: Props): JSX.Element | null => {
  if (!grading) {
    return null;
  }

  const decision = DECISION_META[grading.sendDecision];

  return (
    <section className={'w-full rounded-md border bg-white p-4 shadow space-y-4'}>
      <div className={'flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'}>
        <div className={'space-y-2'}>
          <p className={'text-xs font-semibold uppercase tracking-wide text-gray-500'}>Automated grading review</p>
          <div className={'flex flex-wrap items-center gap-2'}>
            <h3 className={'text-xl font-bold text-gray-900'}>{decision.label}</h3>
            <span className={`rounded-full px-3 py-1 text-sm font-semibold ${decision.pillClassName}`}>
              {humanizeToken(grading.sendDecision)}
            </span>
          </div>
          <p className={'text-sm text-gray-700'}>{decision.description}</p>
        </div>

        <div className={`rounded-md border px-4 py-3 ${decision.accentClassName}`}>
          <p className={'text-sm font-medium'}>Overall score</p>
          <p className={'text-3xl font-bold leading-none text-center'}>{formatScore(grading.overallScore)}</p>
        </div>
      </div>

      <div className={'grid gap-3 md:grid-cols-2 xl:grid-cols-3'}>
        {CATEGORY_ORDER.map((categoryKey) => (
          <div key={categoryKey} className={'rounded-md border bg-gray-50 p-3'}>
            <p className={'text-sm text-gray-600'}>{CATEGORY_LABELS[categoryKey]}</p>
            <p className={'text-lg font-semibold text-gray-900'}>
              {formatScore(grading.categoryScores[categoryKey].score)} / {formatScore(grading.categoryScores[categoryKey].maxScore)}
            </p>
          </div>
        ))}
      </div>

      <div className={'rounded-md border bg-gray-50 p-4'}>
        <p className={'text-sm font-semibold text-gray-900'}>Summary</p>
        <p className={'mt-2 whitespace-pre-line text-sm text-gray-700'}>{grading.summary}</p>
      </div>

      <div className={'rounded-md border bg-gray-50 p-4'}>
        <p className={'text-sm font-semibold text-gray-900'}>Strengths</p>
        {grading.strengths.length > 0 ? (
          <ul className={'mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700'}>
            {grading.strengths.map((strength, index) => (
              <li key={`${strength}-${index}`}>{strength}</li>
            ))}
          </ul>
        ) : (
          <p className={'mt-2 text-sm text-gray-600'}>No specific strengths were listed.</p>
        )}
      </div>

      <div className={'rounded-md border bg-gray-50 p-4'}>
        <p className={'text-sm font-semibold text-gray-900'}>Critical issues</p>
        {grading.criticalIssues.length > 0 ? (
          <div className={'mt-2 space-y-3'}>
            {grading.criticalIssues.map((issue, index) => (
              <div key={`${issue.type}-${index}`} className={'rounded-md border border-red-200 bg-red-50 p-3'}>
                <div className={'flex flex-wrap items-center gap-2'}>
                  <p className={'text-sm font-semibold text-red-900'}>{humanizeToken(issue.type)}</p>
                  <span className={'rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-800'}>
                    {issue.severity}
                  </span>
                </div>
                <p className={'mt-2 text-sm text-red-900'}>{issue.reason}</p>
                <blockquote className={'mt-2 border-l-4 border-red-200 pl-3 text-sm italic text-red-800'}>
                  "{issue.quote}"
                </blockquote>
              </div>
            ))}
          </div>
        ) : (
          <p className={'mt-2 text-sm text-gray-600'}>No critical issues were detected.</p>
        )}
      </div>

      <div className={'rounded-md border bg-gray-50 p-4'}>
        <p className={'text-sm font-semibold text-gray-900'}>Warnings</p>
        {grading.warnings.length > 0 ? (
          <div className={'mt-2 space-y-3'}>
            {grading.warnings.map((warning, index) => (
              <div key={`${warning.type}-${index}`} className={'rounded-md border border-yellow-200 bg-yellow-50 p-3'}>
                <p className={'text-sm font-semibold text-yellow-900'}>{humanizeToken(warning.type)}</p>
                <p className={'mt-1 text-sm text-yellow-900'}>{warning.reason}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className={'mt-2 text-sm text-gray-600'}>No warnings were raised.</p>
        )}
      </div>

      <div className={'flex flex-col gap-1 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between'}>
        <span>Graded with model `{grading.modelId}`</span>
        <span>Last updated {formatDate(grading.updatedAt)}</span>
      </div>
    </section>
  );
};

export { ImpactReportGradingSummary };
